import type { NoticeLevel } from '@shared/designSystem/NoticeBanner'
import type { LocalizedContent } from '@shared/localization/languageForCountryCode'

export type PlatformNoticeScope = 'general' | 'organization'

/**
 * A notice as written by qontact-backend under
 *   /platformNotices/global/{noticeID}
 *   /platformNotices/organizations/{organizationID}/{noticeID}
 * Every user-facing text carries all the languages; the client picks one.
 */
export interface PlatformNotice {
    id: string
    version: number
    level: NoticeLevel
    scope: PlatformNoticeScope
    organizationID?: string
    title: LocalizedContent
    body: LocalizedContent
    cta?: { label: LocalizedContent, href: string }
    requiresAcknowledgement: boolean
    publishedAt: number
    updatedAt: number
    startsAt?: number
    endsAt?: number
}

/** Per-user state under /platformNoticeUserState/{uid}/{noticeID}. */
export interface PlatformNoticeUserState {
    dismissedVersion?: number
    acknowledgedVersion?: number
}

const LEVELS: NoticeLevel[] = ['critical', 'warning', 'communication']

const asNumber = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) ? value : undefined

const asLocalized = (value: unknown): LocalizedContent | undefined => {
    if (!value || typeof value !== 'object') {
        return undefined
    }
    const result: LocalizedContent = {}
    for (const language of ['es', 'pt', 'en'] as const) {
        const text = (value as Record<string, unknown>)[language]
        if (typeof text === 'string' && text.trim()) {
            result[language] = text
        }
    }
    return Object.keys(result).length > 0 ? result : undefined
}

const isSafeHref = (href: string): boolean => /^(https?:\/\/|\/(?!\/))/i.test(href)

/** Validates one raw RTDB node; returns undefined for anything malformed or archived. */
export function parsePlatformNotice(
    id: string,
    raw: unknown,
    scope: PlatformNoticeScope,
    organizationID?: string
): PlatformNotice | undefined {
    if (!raw || typeof raw !== 'object') {
        return undefined
    }
    const node = raw as Record<string, unknown>
    if (node.status !== undefined && node.status !== 'active') {
        return undefined
    }
    const level = LEVELS.find((candidate) => candidate === node.level)
    const title = asLocalized(node.title)
    const body = asLocalized(node.body) ?? {}
    const publishedAt = asNumber(node.publishedAt) ?? asNumber(node.createdAt)
    if (!level || !title || publishedAt === undefined) {
        return undefined
    }

    let cta: PlatformNotice['cta']
    const rawCta = node.cta as Record<string, unknown> | undefined
    if (rawCta && typeof rawCta.href === 'string' && isSafeHref(rawCta.href)) {
        const label = asLocalized(rawCta.label)
        if (label) {
            cta = { label, href: rawCta.href }
        }
    }

    return {
        id,
        version: asNumber(node.version) ?? 1,
        level,
        scope,
        organizationID: scope === 'organization' ? organizationID : undefined,
        title,
        body,
        cta,
        // Critical notices ask for acknowledgement unless the publisher opted out.
        requiresAcknowledgement: level === 'critical' && node.requiresAcknowledgement !== false,
        publishedAt,
        updatedAt: asNumber(node.updatedAt) ?? publishedAt,
        startsAt: asNumber(node.startsAt),
        endsAt: asNumber(node.endsAt),
    }
}

export function parsePlatformNoticeCollection(
    raw: unknown,
    scope: PlatformNoticeScope,
    organizationID?: string
): PlatformNotice[] {
    if (!raw || typeof raw !== 'object') {
        return []
    }
    return Object.entries(raw as Record<string, unknown>)
        .map(([id, value]) => parsePlatformNotice(id, value, scope, organizationID))
        .filter((notice): notice is PlatformNotice => notice !== undefined)
}

export function parsePlatformNoticeUserStates(raw: unknown): Record<string, PlatformNoticeUserState> {
    if (!raw || typeof raw !== 'object') {
        return {}
    }
    const result: Record<string, PlatformNoticeUserState> = {}
    for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
        if (!value || typeof value !== 'object') {
            continue
        }
        const node = value as Record<string, unknown>
        result[id] = {
            dismissedVersion: asNumber(node.dismissedVersion),
            acknowledgedVersion: asNumber(node.acknowledgedVersion),
        }
    }
    return result
}

export const noticeKey = (notice: Pick<PlatformNotice, 'id' | 'version'>): string => `${notice.id}@${notice.version}`
